import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { appointmentsApi, appointmentQueryKeys } from "@/shared/api/appointments";
import { encountersApi, encounterQueryKeys } from "@/shared/api/encounters";
import { Card } from "@/components/ui/card";
import { isoDate } from "@/shared/format";
import { roomScopeApi, roomScopeQueryKeys } from "@/shared/api/queueOps";
import { useState } from "react";

function genderLabel(g: string | null): string {
  return g === "M" ? "Nam" : g === "F" ? "Nữ" : g ? "Khác" : "—";
}

/**
 * Danh sách bệnh nhân — the left rail: today's counters plus the patients currently in exam
 * (TD-7b, #18800).
 *
 * <p><b>Owns its own two server queries.</b> `encounters.queue` and `appointments.forDate` were
 * fetched by the workspace and read by nothing else, so they came down with the markup. They keep
 * the same query keys, which is what makes this safe: the workspace still invalidates
 * `encounterQueryKeys.queue()` after a sign or a queue action, and this rail still refetches — the
 * coupling is the cache key, not a prop.
 *
 * <p>Takes the current encounter id purely to highlight the active row. It is presentation: no
 * mutation, no state.
 */
export function EncounterQueueSidebar({
  currentEncounterId,
  today,
}: {
  currentEncounterId: string;
  /** ISO yyyy-MM-dd — passed in so the rail counts the same day the workspace does. */
  today: string;
}) {
  // #18882 — which rooms this user works in. A one-room branch reports singleRoomBranch and the
  // rail then shows no room UI at all: the owner's rule is that a small clinic should not have to
  // see the concept.
  const scope = useQuery({
    queryKey: roomScopeQueryKeys.scope(null),
    queryFn: () => roomScopeApi.get(null),
    staleTime: 5 * 60_000,
  });
  const myRooms = scope.data?.rooms ?? [];
  const showRoomUi = !!scope.data && !scope.data.singleRoomBranch && myRooms.length > 0;
  // "" = every room I work in. Only offered when there is more than one to combine, or when the
  // user may coordinate — otherwise it is the same list under a second name.
  const [roomFilter, setRoomFilter] = useState("");
  const activeRoom = myRooms.find((r) => r.id === roomFilter) ?? null;

  const queue = useQuery({
    queryKey: encounterQueryKeys.queueRoom(roomFilter || null),
    queryFn: () => encountersApi.queue(roomFilter || null),
  });
  const appts = useQuery({
    queryKey: appointmentQueryKeys.forDate(today),
    queryFn: () => appointmentsApi.forDate(today),
  });

  const list = appts.data ?? [];
  const rows = queue.data ?? [];
  // "Đang khám" counted EVERY unfinished encounter, with no date filter — `GET
  // /encounters/queue` returns OPEN + IN_PROGRESS across all time, capped at 200. The other three
  // tiles count TODAY's appointments, so a clinic with visits never closed off saw "Đang khám 41"
  // beside "Hôm nay 4". Same split the list page has used since #18580: an encounter started
  // before today is carried over, not in progress now.
  const startedToday = (e: (typeof rows)[number]) =>
    !e.startedAt || isoDate(new Date(e.startedAt)) >= today;
  const examToday = rows.filter(startedToday).length;
  // Deliberately still shown rather than filtered away. The old number was wrong, but it was
  // wrong about something real: these are visits nobody ever completed, and dropping them from
  // the rail would make the only signal of that disappear.
  const carriedOver = rows.length - examToday;
  const stats = {
    wait: list.filter((a) => a.status === "CHECKED_IN").length,
    exam: examToday,
    done: list.filter((a) => a.status === "COMPLETED").length,
    today: list.length,
  };

  return (
    <aside className="order-2 lg:order-1">
      <Card className="p-0">
        <div className="border-b px-4 py-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Danh sách bệnh nhân
          </div>
          {showRoomUi &&
            (myRooms.length === 1 && !scope.data?.canViewAll ? (
              // One assigned room and no override: state it, do not offer a choice that has only
              // one outcome.
              <div className="mt-1 text-sm font-medium">{myRooms[0].name}</div>
            ) : (
              <select
                className="mt-1.5 h-8 w-full rounded-md border bg-background px-2 text-sm"
                value={roomFilter}
                onChange={(e) => setRoomFilter(e.target.value)}
                aria-label="Phòng"
              >
                <option value="">
                  {scope.data?.canViewAll ? "Tất cả phòng" : "Các phòng của tôi"}
                </option>
                {myRooms.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
            ))}
        </div>
        <div className="grid grid-cols-2 gap-px border-b bg-border">
          <div className="bg-card p-3 text-center">
            <div className="text-xl font-semibold text-amber-600">{stats.wait}</div>
            <div className="text-[11px] text-muted-foreground">Đang chờ</div>
          </div>
          <div className="bg-card p-3 text-center">
            <div className="text-xl font-semibold text-primary">{stats.exam}</div>
            <div className="text-[11px] text-muted-foreground">Đang khám</div>
          </div>
          <div className="bg-card p-3 text-center">
            <div className="text-xl font-semibold text-foreground">{stats.done}</div>
            <div className="text-[11px] text-muted-foreground">Hoàn thành</div>
          </div>
          <div className="bg-card p-3 text-center">
            <div className="text-xl font-semibold text-foreground">{stats.today}</div>
            <div className="text-[11px] text-muted-foreground">Hôm nay</div>
          </div>
        </div>
        {carriedOver > 0 && (
          <div className="border-b bg-amber-50 px-3 py-2 text-[11px] text-amber-800">
            <b>{carriedOver}</b> lần khám từ ngày trước chưa hoàn tất
            {activeRoom ? ` ở ${activeRoom.name}` : ""} — vẫn nằm trong danh sách dưới đây.
          </div>
        )}
        <div className="max-h-[70vh] space-y-1 overflow-auto p-2">
          {rows.map((q) => (
            <Link
              key={q.id}
              to={`/encounters/${q.id}`}
              className={`flex items-center gap-2 rounded-md border px-2 py-2 text-sm transition-colors hover:bg-accent ${
                q.id === currentEncounterId ? "border-primary bg-primary/5" : "border-transparent"
              }`}
            >
              {/* #19040 — số THẬT của ca, không phải vị trí trong danh sách.
                  Trước đây ô này rơi về `i + 1` khi ca chưa có STT phòng, nên nó trộn hai loại
                  số trong cùng một chỗ và không có nhãn: dòng có STT in "1", "9", "14", dòng
                  không có in "03", "04". Người dùng không phân biệt được, và lọc theo phòng thì
                  cùng một bệnh nhân đổi số. Ghi chú cũ ở đây đã nói đúng — một con số trông như
                  STT mà không phải STT còn tệ hơn là không có — nên giờ KHÔNG in gì khi thiếu,
                  và số thật thì có nhãn. */}
              <span className="w-9 shrink-0 text-center">
                {q.roomNo != null ? (
                  <>
                    <span className="block text-[10px] leading-none text-muted-foreground">
                      STT
                    </span>
                    <span className="block text-sm font-semibold text-foreground">
                      {q.roomNo}
                    </span>
                  </>
                ) : (
                  <span className="block text-xs text-muted-foreground">—</span>
                )}
              </span>
              <span className="flex min-w-0 flex-1 flex-col">
                <b className="truncate">{q.patientName}</b>
                <span className="text-xs text-muted-foreground">
                  {q.patientAge != null ? `${q.patientAge} tuổi · ` : ""}
                  {genderLabel(q.patientGender)}
                </span>
                {/* Panel này LỌC được theo phòng nhưng từng dòng lại không nói phòng nào, nên khi
                    để "Tất cả phòng" thì không phân biệt được ca nào thuộc phòng mình. */}
                <span className="truncate text-xs text-muted-foreground">
                  {q.roomName ?? "Chưa xếp phòng"}
                </span>
              </span>
              <span className="shrink-0 text-xs text-muted-foreground">
                {new Date(q.startedAt).toLocaleTimeString("vi-VN", {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            </Link>
          ))}
          {rows.length === 0 && (
            <p className="px-2 py-4 text-center text-sm text-muted-foreground">
              Chưa có bệnh nhân đang khám.
            </p>
          )}
        </div>
      </Card>
    </aside>
  );
}
